/**
 * Noticing that a player who came back came back as somebody else.
 *
 * A seat is held for fifteen seconds and a reconnect is given the same fifteen, so the two can just
 * miss each other: a guest whose retry lands a moment after the hold expired is seated as a new
 * player with a new number, and the host spawns it a new body. The connection was replaced
 * seamlessly and the player was not, which is the one case the running mode cannot absorb, because
 * `localPlayer` is a fact it was built on.
 *
 * So it is watched for rather than hoped against. The host sends `sessionStart` again on every
 * reconnect, carrying the player id it just seated, and a number that is not this machine's is the
 * signal. The shell answers by rebuilding the mode on the new facts, which is what every reconnect
 * used to do and what this one case still has to.
 *
 * Deferred by a microtask for the same reason [sessionEndWatch](./sessionEndWatch.ts) is: it fires
 * from inside a transport callback whose own listener the answer tears down.
 */

import { decodeStart } from '@/net/wire/startFrame';
import { MESSAGE_KINDS } from '@/net/wire/messageKinds';
import { silentListeners } from '@/net/transportContract';
import type { LobbyGameFacts, NetSession } from '@/net/session/netSession';

export interface SeatChange {
  readonly facts: LobbyGameFacts;
  readonly modeId: string;
}

/**
 * Watches a running session and calls `onChanged` at most once. Returns the function that stops it.
 *
 * A host is watching nobody: it seats everyone else and cannot be reseated.
 */
export function watchSeatChange(
  session: NetSession,
  onChanged: (change: SeatChange) => void,
): () => void {
  if (session.role === 'host') return (): void => undefined;

  let changed = false;

  return session.listenTo({
    ...silentListeners(),

    onMessage: (_from, frame): void => {
      if (frame.kind !== MESSAGE_KINDS.sessionStart || changed) return;

      const start = decodeStart(frame.payload);
      // The same number is the ordinary case: the host says hello again over the new connection and
      // this machine is still who it was, so there is nothing to do about it.
      if (start.playerId === session.localPlayer) return;

      changed = true;
      queueMicrotask(() => {
        onChanged({
          facts: {
            role: 'guest',
            seed: start.seed,
            rules: session.rules,
            localPlayer: start.playerId,
          },
          modeId: start.modeId,
        });
      });
    },
  });
}
